import React, { useState } from 'react';
import { FormattedMessage, FormattedDate, FormattedTime, } from 'react-intl';
import { Button, MessageBanner } from '@folio/stripes/components';
import { ProgressBar } from './ProgressBar';
import { Percent } from './Percent';
var sectionStyle = {
    border: '1px solid #d4dbe3',
    borderRadius: '10px',
    padding: '12px 14px',
    background: '#fff',
    marginBottom: '18px',
};
var toggleStyle = {
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    color: '#1f2d3a',
    font: 'inherit',
    fontSize: '15px',
    fontWeight: 700,
    textAlign: 'left',
};
var M = 'ui-czna-authorities.corpusQuality.validation';
var BODY_ID = 'corpus-validation-body';
/**
 * "Validace záznamů" (AV-03, CZNA-330; folded under the corpus checks by CZNA-1407): the
 * last validation run's date and % valid, its own run button, and — once expanded — the
 * summary strip, layer panels, top rules and trend it always showed. It starts collapsed:
 * the page leads with the link checks, and the validation figures are the second question.
 *
 * <p>The progress bar stays visible while collapsed, so a started run is never invisible.
 * Both runs share one lock; when the checks hold it, the meta line says why the button is off.
 *
 * <p>CZNA-1620: when the validation rules changed after the last completed full revalidation,
 * a warning — also visible while collapsed — gives the time of the change and, to whoever may
 * run it, the recompute of the whole database under the same lock rules as the run button.
 */
export var ValidationSection = function (_a) {
    var latest = _a.latest, isLoading = _a.isLoading, isError = _a.isError, rulesetStatus = _a.rulesetStatus, isRulesetStatusError = _a.isRulesetStatusError, job = _a.job, isRunning = _a.isRunning, isOtherRunning = _a.isOtherRunning, canRun = _a.canRun, isStarting = _a.isStarting, onStart = _a.onStart, children = _a.children;
    var _b = useState(false), open = _b[0], setOpen = _b[1];
    var metaId = 'corpus-validation-meta';
    return (React.createElement("section", { style: sectionStyle, "data-test-run-card": true, "aria-labelledby": "corpus-validation-title" },
        React.createElement("div", { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' } },
            React.createElement("div", { style: { minWidth: 0 } },
                React.createElement("h2", { id: "corpus-validation-title", style: { margin: '0 0 4px' } },
                    React.createElement("button", { type: "button", style: toggleStyle, "aria-expanded": open, "aria-controls": BODY_ID, onClick: function () { return setOpen(!open); }, "data-test-validation-toggle": true },
                        React.createElement("span", { "aria-hidden": true, style: { display: 'inline-block', width: '16px', color: '#5d6b7a' } }, open ? '▾' : '▸'),
                        React.createElement(FormattedMessage, { id: "".concat(M, ".title") }))),
                React.createElement("div", { style: { fontSize: '13px', color: '#43525f', paddingLeft: '16px' }, id: metaId, "data-test-run-card-meta": true },
                    isLoading ? React.createElement(FormattedMessage, { id: "ui-czna-authorities.corpusQuality.loading" }) : null,
                    isError ? (React.createElement("span", { role: "alert", style: { color: '#a4123f' } },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.corpusQuality.latest.loadError" }))) : null,
                    !isLoading && !isError && latest ? (React.createElement(FormattedMessage, { id: "".concat(M, ".dataFrom"), values: {
                            date: latest.finishedAt ? (React.createElement(React.Fragment, null,
                                React.createElement(FormattedDate, { value: latest.finishedAt }),
                                ' ',
                                React.createElement(FormattedTime, { value: latest.finishedAt }))) : '—',
                            percent: React.createElement(Percent, { value: latest.validPercent }),
                        } })) : null,
                    !isLoading && !isError && !latest ? React.createElement(FormattedMessage, { id: "".concat(M, ".never") }) : null,
                    !isRunning && isOtherRunning ? (React.createElement(React.Fragment, null,
                        ' · ',
                        React.createElement(FormattedMessage, { id: "".concat(M, ".otherRunning") }))) : null)),
            canRun ? (React.createElement(Button, { onClick: onStart, disabled: isRunning || isOtherRunning || isStarting, marginBottom0: true, "aria-describedby": metaId, "data-test-run-start": true },
                React.createElement(FormattedMessage, { id: "".concat(M, ".run") }))) : null),
        (rulesetStatus === null || rulesetStatus === void 0 ? void 0 : rulesetStatus.stale) && rulesetStatus.rulesChangedAt ? (React.createElement("div", { style: { marginTop: '12px' }, "data-test-ruleset-stale": true },
            React.createElement(MessageBanner, { type: "warning" },
                React.createElement(FormattedMessage, { id: "".concat(M, ".rulesetStale"), values: {
                        date: (React.createElement(React.Fragment, null,
                            React.createElement(FormattedDate, { value: rulesetStatus.rulesChangedAt }),
                            ' ',
                            React.createElement(FormattedTime, { value: rulesetStatus.rulesChangedAt }))),
                    } }),
                canRun ? (React.createElement("div", { style: { marginTop: '8px' } },
                    React.createElement(Button, { onClick: onStart, disabled: isRunning || isOtherRunning || isStarting, marginBottom0: true, "data-test-ruleset-stale-recompute": true },
                        React.createElement(FormattedMessage, { id: "".concat(M, ".rulesetStale.recompute") })))) : null))) : null,
        isRulesetStatusError ? (React.createElement("div", { style: { marginTop: '12px' }, "data-test-ruleset-status-error": true },
            React.createElement(MessageBanner, { type: "error" },
                React.createElement(FormattedMessage, { id: "".concat(M, ".rulesetStatus.loadError") })))) : null,
        isRunning && job ? (React.createElement("div", { style: { marginTop: '14px' }, "data-test-run-progress": true },
            React.createElement(ProgressBar, { processed: job.processed, total: job.total, errorsFound: job.errorsFound, warningsFound: job.warningsFound }))) : null,
        open ? (React.createElement("div", { id: BODY_ID, style: { marginTop: '16px' }, "data-test-validation-body": true }, children)) : null));
};
